/**
 * Float32 microphone samples -> 16 kHz mono 16-bit WAV.
 *
 * Whisper wants 16 kHz mono anyway, and encoding it in the browser means the
 * server never needs ffmpeg to transcode. It also sidesteps the format split
 * between browsers: MediaRecorder gives webm/opus in Chrome and mp4/aac in
 * Safari, whereas raw PCM is raw PCM everywhere.
 */
export const TARGET_SAMPLE_RATE = 16_000;
/**
 * Box-average decimation. Not a windowed-sinc resampler, but speech at 16 kHz
 * from a 44.1/48 kHz capture is well inside what averaging handles, and it
 * avoids shipping a DSP library to a fridge.
 */
export function downsample(input, inputRate, targetRate = TARGET_SAMPLE_RATE) {
    if (inputRate === targetRate)
        return input;
    if (inputRate < targetRate)
        throw new Error(`Cannot upsample ${inputRate} -> ${targetRate}`);
    const ratio = inputRate / targetRate;
    const outLength = Math.floor(input.length / ratio);
    const out = new Float32Array(outLength);
    for (let i = 0; i < outLength; i++) {
        const start = Math.floor(i * ratio);
        const end = Math.min(input.length, Math.floor((i + 1) * ratio));
        let sum = 0;
        for (let j = start; j < end; j++)
            sum += input[j];
        out[i] = end > start ? sum / (end - start) : 0;
    }
    return out;
}
/** Concatenates the chunks an AudioWorklet delivers. */
export function concatFloat32(chunks) {
    let total = 0;
    for (const c of chunks)
        total += c.length;
    const out = new Float32Array(total);
    let offset = 0;
    for (const c of chunks) {
        out.set(c, offset);
        offset += c.length;
    }
    return out;
}
/** Peak-normalised RMS, 0..1 — drives the level bars on the scan screen. */
export function rms(samples) {
    if (samples.length === 0)
        return 0;
    let sum = 0;
    for (let i = 0; i < samples.length; i++)
        sum += samples[i] * samples[i];
    return Math.sqrt(sum / samples.length);
}
export function encodeWav(samples, sampleRate = TARGET_SAMPLE_RATE) {
    const bytesPerSample = 2;
    const blockAlign = bytesPerSample; // mono
    const dataBytes = samples.length * bytesPerSample;
    const buffer = new ArrayBuffer(44 + dataBytes);
    const view = new DataView(buffer);
    const writeAscii = (offset, text) => {
        for (let i = 0; i < text.length; i++)
            view.setUint8(offset + i, text.charCodeAt(i));
    };
    writeAscii(0, 'RIFF');
    view.setUint32(4, 36 + dataBytes, true);
    writeAscii(8, 'WAVE');
    writeAscii(12, 'fmt ');
    view.setUint32(16, 16, true); // PCM chunk size
    view.setUint16(20, 1, true); // format: PCM
    view.setUint16(22, 1, true); // channels: mono
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * blockAlign, true); // byte rate
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, 8 * bytesPerSample, true);
    writeAscii(36, 'data');
    view.setUint32(40, dataBytes, true);
    let offset = 44;
    for (let i = 0; i < samples.length; i++) {
        const clamped = Math.max(-1, Math.min(1, samples[i]));
        // Asymmetric range: -32768..32767.
        view.setInt16(offset, clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff, true);
        offset += 2;
    }
    return buffer;
}
